import { z } from "zod";

/**
 * Reading an answer a newer server wrote. The contract's schemas are strict, which is right for
 * what a server writes and its tests check, and wrong for what an open page reads: a server that
 * adds a field to a message would make every tab opened before it refuse the chat until it
 * reloaded. A client therefore reads what it knows and leaves out what it does not.
 *
 * Only fields are left out. A value that is missing, of the wrong type or of a kind this build has
 * never heard of is still refused, as is a value that is wrong beside one that is only extra, so
 * this never reads something it could not have read before.
 */

/** Each pass takes out the fields a pass found extra; a deeper union can need a second look. */
const MAX_PASSES = 4;

type Failure = z.ZodSafeParseError<unknown>;

/** The object at `path` in `value`, or null when the path leads nowhere an object is. */
function objectAt(
  value: unknown,
  path: readonly PropertyKey[],
): Record<PropertyKey, unknown> | null {
  let here: unknown = value;
  for (const step of path) {
    if (typeof here !== "object" || here === null) return null;
    here = (here as Record<PropertyKey, unknown>)[step];
  }
  return typeof here === "object" && here !== null ? (here as Record<PropertyKey, unknown>) : null;
}

/**
 * `schema`'s answer for `input` with the fields it does not know taken out first, wherever they
 * are. `input` is not changed. Any other fault, or a field that is extra beside one that is wrong,
 * is the schema's own failure.
 */
export function parseKnownFields<S extends z.ZodType>(
  schema: S,
  input: unknown,
): z.ZodSafeParseResult<z.output<S>> {
  let value: unknown = structuredClone(input);
  let result = schema.safeParse(value);
  for (let pass = 0; pass < MAX_PASSES && !result.success; pass += 1) {
    const { issues } = result.error;
    if (!issues.every((issue) => issue.code === "unrecognized_keys")) return result;
    for (const issue of issues) {
      if (issue.code !== "unrecognized_keys") continue;
      const holder = objectAt(value, issue.path);
      if (holder === null) return result;
      for (const key of issue.keys) delete holder[key];
    }
    result = schema.safeParse(value);
  }
  return result;
}

/**
 * `schema` as a client reads it: the same schema for what it checks, and a value with the fields
 * it does not know left out, as `parseKnownFields` does. Fits where a contract takes an output schema.
 */
export function knownFields<S extends z.ZodType>(schema: S): z.ZodType<z.output<S>> {
  return z.unknown().transform((value, context): z.output<S> => {
    const parsed = parseKnownFields(schema, value);
    if (parsed.success) return parsed.data;
    for (const issue of (parsed as Failure).error.issues) {
      context.addIssue({ code: "custom", path: issue.path, message: issue.message });
    }
    return z.NEVER;
  });
}
